<template>
  <div
    class="relative flex bg-login-bg min-h-screen text-sm"
    data-cy="dashboard-nav"
  >
    <div
      class="group/dashboard-nav relative z-50 flex min-h-screen flex-col justify-between"
      :class="{ 'w-[200px]': sidebarExpanded }"
    >
      <expand-button
        :expanded="sidebarExpanded"
        class="invisible absolute right-0 z-50 mt-2-5 -mr-2-5 cursor-pointer group-hover/dashboard-nav:visible"
        data-cy="dashboard-nav-expand-button"
        @click="toggleSidebar"
      />
      <div
        class="flex flex-1 min-h-0 flex-col divide-y divide-gray-700 space-y-3"
      >
        <router-link
          class="cursor-pointer focus:outline-hidden px-5 py-8"
          :class="[{ 'pt-18': props.testMode }]"
          to="/"
        >
          <icon-base
            v-if="sidebarExpanded"
            class="w-25 h-4"
            height="18"
            width="120"
            icon="logo-expanded"
            :icon-name="$t('COMMON.LOGO')"
          />
          <icon-base
            v-else
            class="w-[18px] h-[23px]"
            view-box="3.5 0 21.25 28"
            icon="logo"
            :icon-name="$t('COMMON.LOGO')"
          />
        </router-link>

        <div class="overflow-y-auto space-y-2 overflow-x-hidden">
          <access-control lendflow clients>
            <sidebar-button
              :sidebar-expanded="sidebarExpanded"
              data-cy="dashboard-nav-cmd-k-button"
              :tooltip-label="$t('AI_AGENTS.COMMAND.TITLE')"
              @click="toggleCmdK"
            >
              <template #icon>
                <div class="flex size-4 flex-none items-center justify-center">
                  <icon-base
                    class="icon-command"
                    view-box="2 2 20 20"
                    :icon="IconCommand"
                    icon-color="white"
                  />
                </div>
              </template>
              <template #label>
                <div class="flex items-center justify-between">
                  <span>
                    {{ $t("AI_AGENTS.COMMAND.TITLE") }}
                  </span>
                  <div class="flex space-x-1">
                    <span class="keyboard-hint" :class="{ wide: !isMacOs }">
                      {{ metaKey }}
                    </span>
                    <span class="keyboard-hint">K</span>
                  </div>
                </div>
              </template>
            </sidebar-button>
          </access-control>

          <access-control :funders="true">
            <sidebar-button
              v-if="isNotLendflowOrClientUser"
              to="/funding-partners/details"
              :sidebar-expanded="sidebarExpanded"
              class="relative"
              :tooltip-label="$t('SIDEBAR.DETAILS')"
            >
              <template #icon>
                <div class="flex size-4 justify-center">
                  <icon-base
                    class="icon-business"
                    view-box="10 10 20 20"
                    icon="businesses"
                    icon-color="white"
                  />
                </div>
              </template>
              <template #label>
                {{ $t("SIDEBAR.DETAILS") }}
              </template>
            </sidebar-button>
          </access-control>

          <access-control :lendflow="true" :clients="true">
            <sidebar-button
              to="/deals"
              class="group"
              :sidebar-expanded="sidebarExpanded"
              :tooltip-label="$t('SIDEBAR.DEALS.TITLE')"
              :active-condition="dealsActiveCondition"
            >
              <recent-submenu
                v-show="dealsHistory.length"
                class="absolute left-full z-40 hidden group-hover:block transition-all"
                :recent-list="recentSubmenuList"
                placeholder="Search Deals"
              />
              <template #icon>
                <div class="flex size-4 justify-center">
                  <icon-base
                    class="icon-deals"
                    view-box="9 11 21 18"
                    icon="deals"
                    icon-color="white"
                  />
                </div>
              </template>
              <template #label>
                {{ $t("SIDEBAR.DEALS.TITLE") }}
              </template>
            </sidebar-button>
          </access-control>
          <access-control lendflow :clients="canClientAccessActivityHub">
            <sidebar-button
              to="/inbox"
              class=""
              :sidebar-expanded="sidebarExpanded"
              :tooltip-label="$t('COMMON.INBOX')"
              :active-condition="inboxActiveCondition"
            >
              <template #icon>
                <div class="flex size-4 items-center justify-center">
                  <icon-base
                    class="icon-activity-hub"
                    view-box="4 1 17 20"
                    :icon="IconActivityHub"
                  />
                </div>
              </template>
              <template #label>{{ $t("COMMON.INBOX") }}</template>
            </sidebar-button>
          </access-control>
          <access-control lendflow>
            <sidebar-button
              to="/funders"
              :sidebar-expanded="sidebarExpanded"
              class="relative"
              :tooltip-label="$t('SIDEBAR.FUNDERS')"
              :active-condition="fundersActiveCondition"
            >
              <template #icon>
                <div class="flex size-4 justify-center">
                  <icon-base
                    class="icon-funders"
                    view-box="11 11 18 18"
                    icon="funders"
                    icon-color="white"
                  />
                </div>
              </template>
              <template #label>
                {{ $t("SIDEBAR.FUNDERS") }}
              </template>
            </sidebar-button>
          </access-control>
          <sidebar-button
            v-if="canViewBusinessEntityProfiles && !auth.isAccelerateTaxViewer"
            to="/businesses"
            :sidebar-expanded="sidebarExpanded"
            class="relative"
            :tooltip-label="$t('BUSINESS_PROFILES.MAIN_ROUTE_NAME')"
            :active-condition="businessesActiveCondition"
            data-cy="businesses-profiles-nav"
          >
            <template #icon>
              <div class="flex size-4 items-center justify-center">
                <icon-base
                  class="icon-business"
                  view-box="10 10 20 20"
                  icon="businesses"
                />
              </div>
            </template>
            <template #label>
              {{ $t("BUSINESS_PROFILES.MAIN_ROUTE_NAME") }}
            </template>
          </sidebar-button>
          <sidebar-button
            v-if="
              canViewIndividualEntityProfiles && !auth.isAccelerateTaxViewer
            "
            to="/individuals"
            :sidebar-expanded="sidebarExpanded"
            class="relative"
            :active-condition="individualProfilesActiveCondition"
            :tooltip-label="$t('INDIVIDUAL_PROFILES.MAIN_ROUTE_NAME')"
          >
            <template #icon>
              <div class="flex size-4 items-center justify-center">
                <icon-base
                  class="icon-persons"
                  view-box="9 10 23 21"
                  icon="persons"
                />
              </div>
            </template>
            <template #label>
              {{ $t("INDIVIDUAL_PROFILES.MAIN_ROUTE_NAME") }}
            </template>
          </sidebar-button>

          <sidebar-button
            id="builders-nav-item"
            :to="{ name: ROUTE_BUILDERS_BASE }"
            :sidebar-expanded="sidebarExpanded"
            :tooltip-label="$t('BUILDERS.TITLE')"
            class="relative"
            data-cy="sidebar-builders"
          >
            <template #icon>
              <div class="flex size-4 items-center justify-center">
                <icon-base
                  class="icon-builders"
                  view-box="3 3 18 18"
                  :icon="IconBuildersNav"
                />
              </div>
            </template>
            <template #label>
              {{ $t("BUILDERS.TITLE") }}
            </template>
          </sidebar-button>

          <access-control :clients="true">
            <sidebar-button
              to="/integrations"
              :sidebar-expanded="sidebarExpanded"
              class="relative"
              tooltip-label="Integrations"
            >
              <template #icon>
                <div class="flex size-4 justify-center">
                  <icon-base
                    class="icon-integrations"
                    view-box="10 10 20 20"
                    icon="integrations"
                    icon-color="white"
                  />
                </div>
              </template>
              <template #label>{{ $t("SIDEBAR.INTEGRATIONS") }}</template>
            </sidebar-button>
          </access-control>
          <access-control :roles="[CLIENT_ADMIN]">
            <sidebar-button
              to="/customize"
              :sidebar-expanded="sidebarExpanded"
              class="relative"
              tooltip-label="Customize"
            >
              <template #icon>
                <div class="flex size-4 justify-center">
                  <icon-base
                    class="icon-customize"
                    view-box="11 10 18 20"
                    icon="customize"
                    icon-color="white"
                  />
                </div>
              </template>
              <template #label>{{ $t("SIDEBAR.CUSTOMIZE") }}</template>
            </sidebar-button>
          </access-control>
          <access-control :roles="reportsAccessRoles">
            <sidebar-button
              to="/reports/"
              :sidebar-expanded="sidebarExpanded"
              class="relative"
              tooltip-label="Reports"
            >
              <template #icon>
                <div class="flex size-4 justify-center">
                  <icon-base
                    class="icon-reports"
                    view-box="10 9 22 20"
                    icon="reports"
                    icon-color="white"
                  />
                </div>
              </template>
              <template #label>{{ $t("SIDEBAR.REPORTS") }}</template>
            </sidebar-button>
          </access-control>
          <sidebar-button
            to="/ai_agents"
            :sidebar-expanded="sidebarExpanded"
            class="relative"
            :tooltip-label="$t('AI_AGENTS.TITLE')"
            data-cy="ai-agents-nav"
          >
            <template #icon>
              <div class="flex size-4 items-center justify-center">
                <icon-base
                  class="icon-ai"
                  view-box="0 2 24 20"
                  :icon="IconAINav"
                />
              </div>
            </template>
            <template #label>
              {{ $t("AI_AGENTS.TITLE") }}
            </template>
          </sidebar-button>
        </div>
      </div>
      <div class="flex flex-col justify-end">
        <access-control lendflow>
          <sidebar-button
            :sidebar-expanded="sidebarExpanded"
            class="relative mb-2"
            :class="{
              'rounded-xs bg-white/10 text-white': isQuickActionsBarVisible,
            }"
            :tooltip-label="quickActionsTooltip"
            data-cy="quick-actions-button"
            @click="toggleQuickActions"
          >
            <template #icon>
              <div class="flex size-4 items-center justify-center">
                <icon-base
                  class="icon-quick-actions"
                  view-box="6 2 12 20"
                  :icon="IconQuickActions"
                  icon-color="white"
                  :class="{ 'text-white': isQuickActionsBarVisible }"
                />
              </div>
            </template>
            <template #label>
              {{ $t("QUICK_ACTIONS.TITLE") }}
            </template>
          </sidebar-button>
        </access-control>
        <div class="flex flex-col p-2">
          <template v-if="isPiPSupported">
            <sidebar-button
              :sidebar-expanded="sidebarExpanded"
              class="relative mx-0 mb-2"
              :tooltip-label="$t('DIALER.TITLE')"
              data-cy="twilio-pip-button"
              @click="openTwilioPiP"
            >
              <template #icon>
                <div class="relative flex size-4 items-center justify-center">
                  <icon-base
                    class="icon-phone"
                    view-box="0 0 16 16"
                    :icon="IconDialerPhone"
                    icon-color="white"
                  />
                  <span
                    v-if="!isDeviceIdle"
                    class="absolute -right-1 -top-1 size-2 rounded-full bg-success animate-pulse"
                  />
                </div>
              </template>
              <template #label>
                {{ $t("DIALER.TITLE") }}
              </template>
            </sidebar-button>
            <sidebar-button
              v-if="!isDeviceIdle"
              :sidebar-expanded="sidebarExpanded"
              class="relative mx-0 mb-2"
              :tooltip-label="$t('DIALER.RESET_STATE')"
              data-cy="twilio-reset-button"
              @click.stop="callStore.resetCallState()"
            >
              <template #icon>
                <div class="flex size-4 items-center justify-center">
                  <icon-base
                    :icon="IconReset"
                    width="12"
                    height="12"
                    view-box="0 0 16 16"
                    icon-color="white"
                  />
                </div>
              </template>
              <template #label>
                {{ $t("DIALER.RESET_STATE") }}
              </template>
            </sidebar-button>
          </template>
          <sidebar-test-mode-button-with-modal :test-mode="props.testMode" />
        </div>
        <profile-block :sidebar-expanded="sidebarExpanded" />
      </div>
    </div>
  </div>
  <cmd-k
    v-if="agentSessionInfo.isShown"
    @close="() => (agentSessionInfo.isShown = false)"
  />
  <quick-actions v-if="isQuickActionsBarVisible" />
</template>
<script lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { useStore } from "vuex";

import { storeToRefs } from "pinia";

import { Ability, PermissionSubject } from "@/enums/auth";
import { ShortcutAction } from "@/enums/keyboardShortcuts";
import { usePermissions } from "@/hooks/auth";
import { useAuth } from "@/hooks/auth";
import { useResponsive } from "@/hooks/common";
import { useTour } from "@/hooks/common";
import { waitForElement } from "@/hooks/elements";
import { useKeyboardShortcuts } from "@/hooks/keyboardShortcuts";
import keyValueService, { KeyId } from "@/services/modules/keyValue";
import { useAiAgentsStore } from "@/stores/ai/agents";
import { useCommunicationStore } from "@/stores/communication";
import useQuickActionsStore from "@/stores/quickActions";

import IconBuildersNav from "./icons/IconBuildersNav.vue";
import IconAINav from "@/components/icons/IconAINav.vue";

import type { IDealHistoryRecord } from "@/models/state";
import type { IUser } from "@/models/users";
</script>
<script setup lang="ts">
import {
  ADMIN,
  CLIENT_ADMIN,
  CLIENT_ROLE_GROUP,
  LENDFLOW_ROLE_GROUP,
  SUPER_ADMIN,
  USER,
} from "@/helpers/constants";
import { TWILIO_PIP_OPEN } from "@/helpers/constants/events";
import { useEmitter } from "@/hooks/common";
import {
  ROUTE_BUILDERS_BASE,
  ROUTE_BUSINESSES,
  ROUTE_BUSINESS_ENTITY_PROFILE,
  ROUTE_BUSINESS_PROFILE_DEALS,
  ROUTE_BUSINESS_PROFILE_INFO,
  ROUTE_BUSINESS_RELATIONS,
  ROUTE_BUSINESS_TRACKING,
  ROUTE_INBOX,
  ROUTE_INDIVIDUAL_PROFILE,
  ROUTE_INDIVIDUAL_PROFILE_DEALS,
  ROUTE_INDIVIDUAL_PROFILE_PERSONAL_INFO,
  ROUTE_INDIVIDUAL_PROFILE_TRACKING,
  ROUTE_INDIVIDUAL_RELATIONS,
} from "@/router/routes";
import { useCallsStore } from "@/stores/calls";
import { useEditorStore } from "@/stores/editor";

import AccessControl from "@/components/AccessControl.vue";
import QuickActions from "@/components/communication/standalone/QuickActions.vue";
import IconActivityHub from "@/components/icons/IconActivityHub.vue";
import IconCommand from "@/components/icons/IconCommand.vue";
import IconDialerPhone from "@/components/icons/IconDialerPhone.vue";
import IconQuickActions from "@/components/icons/IconQuickActions.vue";
import IconReset from "@/components/icons/IconReset.vue";
import CmdK from "@/components/modals/CmdK.vue";
import ProfileBlock from "@/components/sidebar/ProfileBlock.vue";
import RecentSubmenu from "@/components/sidebar/RecentSubmenu.vue";
import SidebarButton from "@/components/sidebar/SidebarButton.vue";
import SidebarTestModeButtonWithModal from "@/components/sidebar/SidebarTestModeButtonWithModal.vue";
import IconBase from "@/components/ui/IconBase.vue";
import ExpandButton from "@/components/ui/buttons/ExpandButton.vue";

const props = defineProps({
  testMode: {
    type: Boolean,
    default: false,
  },
});

const route = useRoute();
const { t } = useI18n();
const { getters, commit } = useStore();
const { canPerformActionReactive } = usePermissions();
const communcationStore = useCommunicationStore();
const aiAgentsStore = useAiAgentsStore();
const editorStore = useEditorStore();
const quickActionsStore = useQuickActionsStore();
const { isSmallDesktop } = useResponsive();

const { canClientAccessActivityHub } = storeToRefs(communcationStore);
const { agentSessionInfo } = storeToRefs(aiAgentsStore);
const { isFocused } = storeToRefs(editorStore);
const { isVisible: isQuickActionsBarVisible } = storeToRefs(quickActionsStore);

// Twilio PiP state from store (synced across tabs)
const callStore = useCallsStore();
const { isDeviceIdle, isPiPSupported } = storeToRefs(callStore);

const auth = useAuth();
const { assignShortcutToAction, isMacOs } = useKeyboardShortcuts();

const [canViewIndividualEntityProfiles, canViewBusinessEntityProfiles] = [
  PermissionSubject.individualProfile,
  PermissionSubject.businessProfile,
].map((permissionSubject) => {
  return canPerformActionReactive(permissionSubject, Ability.viewAny, {});
});

const settings = computed(() => getters["options/settings"]);
const user = computed<IUser>(() => getters["auth/user"]);

const sidebarExpanded = ref<boolean>(
  settings.value.dashboardMenuExpanded ?? !isSmallDesktop.value
);

const { nextTourStep } = useTour({
  steps: [
    {
      title: t("BUILDERS.ONBOARDING.TITLE"),
      description: t("BUILDERS.ONBOARDING.DESCRIPTION"),
      selector: "#builders-nav-item",
      placement: "right",
    },
  ],
  onTourEnd: () => {
    keyValueService.kvRequest(KeyId.BuildersNavigationSectionTourSeen, true);
  },
});

const dealsHistory = computed(() =>
  (getters["dealsHistoryStack"] as IDealHistoryRecord[]).map((deal) => ({
    label: deal.name,
    link: `/deals/${deal.id}/progress`,
    status: deal.status,
  }))
);

const dealsActiveCondition = computed(() =>
  routeIncludesNames([
    "DealsBaseView",
    "Deals",
    "DealActivity",
    "DealProgress",
    "FullApp",
    "DealDocuments",
    "Tracking",
    "ApplicationLinks",
  ])
);

const inboxActiveCondition = computed(() => routeIncludesNames([ROUTE_INBOX]));

const businessesActiveCondition = computed(() =>
  routeIncludesNames([
    ROUTE_BUSINESSES,
    ROUTE_BUSINESS_ENTITY_PROFILE,
    ROUTE_BUSINESS_PROFILE_DEALS,
    ROUTE_BUSINESS_PROFILE_INFO,
    ROUTE_BUSINESS_RELATIONS,
    ROUTE_BUSINESS_TRACKING,
  ])
);

const individualProfilesActiveCondition = computed(() =>
  routeIncludesNames([
    ROUTE_INDIVIDUAL_PROFILE,
    ROUTE_INDIVIDUAL_PROFILE_PERSONAL_INFO,
    ROUTE_INDIVIDUAL_PROFILE_DEALS,
    ROUTE_INDIVIDUAL_PROFILE_TRACKING,
    ROUTE_INDIVIDUAL_RELATIONS,
  ])
);

const fundersActiveCondition = computed(() =>
  routeIncludesNames(["Funders", "FunderInfo"])
);

const recentSubmenuList = computed(() => {
  return {
    name: t("SIDEBAR.RECENT_DEALS"),
    items: dealsHistory.value,
  };
});

const isUserClient = computed(() => {
  if (user.value.roles) {
    return user.value.roles.some((role) => CLIENT_ROLE_GROUP.includes(role));
  }
  return false;
});

const isUserLendflowEmployee = computed(() => {
  if (user.value.roles) {
    return user.value.roles.some((role) => LENDFLOW_ROLE_GROUP.includes(role));
  }
  return false;
});

const isNotLendflowOrClientUser = computed(() => {
  return !isUserLendflowEmployee.value && !isUserClient.value;
});

const reportsAccessRoles = computed(() => {
  const workflowsAssigned =
    auth.currentUser?.permissions_preset?.assigned_workflow_template_ids || [];
  const roles = [ADMIN, SUPER_ADMIN, CLIENT_ADMIN];
  if (auth.isAccelerateTaxViewer && workflowsAssigned.length) {
    roles.push(USER);
  }
  return roles;
});

const metaKey = computed(() => (isMacOs ? "⌘" : "CTRL"));

const quickActionsTooltip = computed(() => {
  const meta = metaKey.value;
  const keyClass =
    "flex size-4 items-center justify-center rounded p-1 border border-label text-label";
  const container = document.createElement("div");
  container.className = "flex items-center justify-between gap-2";

  const textSpan = document.createElement("span");
  textSpan.className = "text-white";
  textSpan.textContent = t("QUICK_ACTIONS.OPEN_TOOLTIP");

  const keysContainer = document.createElement("div");
  keysContainer.className = "flex space-x-1";

  [meta, "/"].forEach((key) => {
    const span = document.createElement("span");
    span.className = keyClass;
    span.textContent = key;
    keysContainer.appendChild(span);
  });

  [textSpan, keysContainer].forEach((item) => {
    container.appendChild(item);
  });

  return container.outerHTML;
});

const routeIncludesNames = (routeNames: string[]) => {
  return routeNames.includes(route.name?.toString() ?? "");
};

const toggleSidebar = () => {
  sidebarExpanded.value = !sidebarExpanded.value;
  commit("options/setSettings", {
    key: "dashboardMenuExpanded",
    value: sidebarExpanded.value,
  });
};

const toggleCmdK = () => {
  if (
    (agentSessionInfo.value.isPinned && agentSessionInfo.value.isShown) ||
    isFocused.value
  ) {
    return;
  }

  agentSessionInfo.value.isShown = !agentSessionInfo.value.isShown;
};

const toggleQuickActions = () => {
  isQuickActionsBarVisible.value = !isQuickActionsBarVisible.value;
};

assignShortcutToAction({ actionName: ShortcutAction.ToggleCommandBar, action: toggleCmdK });
assignShortcutToAction({
  actionName: ShortcutAction.ToggleQuickActions,
  action: toggleQuickActions,
  canPerformAction: () => auth.isLendflowUser,
});
assignShortcutToAction({ actionName: ShortcutAction.ToggleSidebar, action: toggleSidebar });

const emitter = useEmitter();

const openTwilioPiP = async () => {
  emitter.emit(TWILIO_PIP_OPEN);
};

onMounted(async () => {
  await waitForElement(".icon-builders");
  const { value: buildersTourSeen } = await keyValueService.kvRequest<
    boolean | null
  >(KeyId.BuildersNavigationSectionTourSeen);
  if (!buildersTourSeen) {
    await nextTourStep();
  }
});
</script>

<style scoped>
@reference "@/assets/tailwind.css";

:deep() {
  .icon-dashboard .left,
  .icon-deals .left,
  .icon-activity-hub .board,
  .icon-funders .top,
  .icon-business .bottom,
  .icon-persons .left,
  .icon-integrations .left,
  .icon-customize .top,
  .icon-reports .arrow,
  .icon-ai,
  .icon-builders .puzzle-right,
  .icon-command .objects,
  .icon-profile,
  .icon-phone,
  .icon-quick-actions {
    @apply transition-all duration-500 group-hover/sidebar:text-white;
  }
}
:deep(.router-link-active) {
  .icon-dashboard .left,
  .icon-deals .left,
  .icon-activity-hub .board,
  .icon-funders .top,
  .icon-business .bottom,
  .icon-persons .left,
  .icon-integrations .left,
  .icon-customize .top,
  .icon-reports .arrow,
  .icon-ai,
  .icon-builders .puzzle-right,
  .icon-command .objects,
  .icon-profile,
  .icon-phone,
  .icon-quick-actions {
    @apply text-white;
  }
}

.keyboard-hint {
  @apply flex size-4 items-center justify-center rounded-xs bg-white/10;
  &.wide {
    @apply w-auto;
  }
}
</style>
